import { FilesetResolver, ImageSegmenter } from '@mediapipe/tasks-vision'

import { BACKGROUND_BLUR_STRENGTH_DEFAULT } from '@/entities/preference'

export const MEDIAPIPE_WASM_PATH = `${import.meta.env.BASE_URL}mediapipe/wasm`
export const SELFIE_SEGMENTER_MODEL_PATH = `${import.meta.env.BASE_URL}models/selfie_segmenter.tflite`

export type BackgroundBlurMode = 'none' | 'blur' | 'image'

export type BackgroundBlurPipelineOptions = {
  blurRadius?: number
  mode?: BackgroundBlurMode
  fps?: number
}

export type BackgroundBlurPipelineHandle = {
  stream: MediaStream
  setMode: (mode: BackgroundBlurMode) => void
  setBlurRadius: (radius: number) => void
  setBackgroundImage: (bitmap: ImageBitmap) => void
  stop: () => void
}

type MediaStreamTrackProcessorCtor = new (init: { track: MediaStreamTrack }) => {
  readable: ReadableStream<VideoFrame>
}

type CanvasCaptureTrack = MediaStreamTrack & {
  requestFrame?: () => void
}

let segmenterPromise: Promise<ImageSegmenter> | null = null

export const isBackgroundBlurSupported = (): boolean => {
  return typeof (globalThis as typeof globalThis & {
    MediaStreamTrackProcessor?: unknown
  }).MediaStreamTrackProcessor === 'function'
    && typeof HTMLCanvasElement !== 'undefined'
    && typeof HTMLCanvasElement.prototype.captureStream === 'function'
    && typeof Worker !== 'undefined'
}

const getImageSegmenter = async (): Promise<ImageSegmenter> => {
  if (!segmenterPromise) {
    segmenterPromise = (async () => {
      const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH)
      return ImageSegmenter.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: SELFIE_SEGMENTER_MODEL_PATH,
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        outputCategoryMask: true,
      })
    })().catch((error) => {
      segmenterPromise = null
      throw error
    })
  }
  return segmenterPromise
}

const mountOutputCanvas = (canvas: HTMLCanvasElement, width: number, height: number) => {
  // Real CSS size + translate: Chromium needs a composited canvas for reliable captureStream.
  // Do NOT use opacity:0 / visibility:hidden / 1×1 — that yields a muted track.
  Object.assign(canvas.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: `${width}px`,
    height: `${height}px`,
    transform: 'translate(-10000px, -10000px)',
    pointerEvents: 'none',
    zIndex: '-1',
  })
  canvas.setAttribute('aria-hidden', 'true')
  document.body.appendChild(canvas)
}

/**
 * MVP pipeline:
 * MediaStreamTrackProcessor → MediaPipe mask (main) → WebGL worker → putImageData → captureStream(fps)
 *
 * Important: canvas bitmap size must be set BEFORE captureStream(). Resizing afterwards
 * clears the buffer and breaks the captured track in Chromium (black / never-loaded <video>).
 */
export const createBackgroundBlurPipeline = async (
  sourceStream: MediaStream,
  options: BackgroundBlurPipelineOptions = {},
): Promise<BackgroundBlurPipelineHandle> => {
  if (!isBackgroundBlurSupported()) {
    throw new Error('Background blur is not supported in this browser')
  }

  const sourceTrack = sourceStream.getVideoTracks()[0]
  if (!sourceTrack) {
    throw new Error('Source stream has no video track')
  }

  const Processor = (globalThis as typeof globalThis & {
    MediaStreamTrackProcessor: MediaStreamTrackProcessorCtor
  }).MediaStreamTrackProcessor

  const fps = options.fps ?? 30
  let currentMode: BackgroundBlurMode = options.mode ?? 'blur'
  let blurRadius = options.blurRadius ?? BACKGROUND_BLUR_STRENGTH_DEFAULT
  let stopped = false
  let frameCount = 0
  let currentSkipFrames = 2
  let lastMaskBuffer: Uint8Array | null = null
  const TARGET_FRAME_TIME = 1000 / 30
  const ML_TIME_THRESHOLD_HIGH = TARGET_FRAME_TIME * 0.8
  const ML_TIME_THRESHOLD_LOW = TARGET_FRAME_TIME * 0.3

  const segmenter = await getImageSegmenter()

  const worker = new Worker(
    new URL('../../../shared/workers/blur-worker.js', import.meta.url),
    { type: 'module' },
  )
  worker.postMessage({ type: 'changeMode', mode: currentMode })
  worker.postMessage({ type: 'changeBlurRadius', radius: String(blurRadius) })

  const processor = new Processor({ track: sourceTrack })
  const reader = processor.readable.getReader()

  // Resolve exact frame size from the first VideoFrame before captureStream.
  // Timeout: a stuck processor must not block call setup for Chromium's ~10s capture timeout.
  const FIRST_FRAME_TIMEOUT_MS = 4000
  let firstFrame: VideoFrame
  let firstFrameTimeoutId: number | undefined
  try {
    const firstRead = await Promise.race([
      reader.read(),
      new Promise<never>((_, reject) => {
        firstFrameTimeoutId = window.setTimeout(
          () => reject(new Error('Timeout waiting for first camera frame for blur')),
          FIRST_FRAME_TIMEOUT_MS,
        )
      }),
    ])
    if (firstFrameTimeoutId !== undefined) {
      window.clearTimeout(firstFrameTimeoutId)
    }
    if (firstRead.done || !firstRead.value) {
      worker.terminate()
      throw new Error('Camera track produced no frames')
    }
    firstFrame = firstRead.value
  } catch (error) {
    if (firstFrameTimeoutId !== undefined) {
      window.clearTimeout(firstFrameTimeoutId)
    }
    void reader.cancel().catch(() => undefined)
    worker.terminate()
    throw error
  }
  const outputWidth = firstFrame.displayWidth || sourceTrack.getSettings().width || 640
  const outputHeight = firstFrame.displayHeight || sourceTrack.getSettings().height || 480

  const outputCanvas = document.createElement('canvas')
  outputCanvas.width = outputWidth
  outputCanvas.height = outputHeight
  mountOutputCanvas(outputCanvas, outputWidth, outputHeight)
  const outputCtx = outputCanvas.getContext('2d', { willReadFrequently: true })
  if (!outputCtx) {
    firstFrame.close()
    worker.terminate()
    throw new Error('Failed to get 2d context for blur output')
  }

  // Fixed-size canvas then captureStream. Use fps>0 so frames flow even when
  // requestFrame is unavailable; still call requestFrame after paint when present.
  const captured = outputCanvas.captureStream(fps)
  const processedTrack = captured.getVideoTracks()[0] as CanvasCaptureTrack | undefined
  if (!processedTrack) {
    firstFrame.close()
    worker.terminate()
    outputCanvas.remove()
    throw new Error('captureStream produced no video track')
  }
  processedTrack.enabled = true

  const pushFrame = () => {
    processedTrack.requestFrame?.()
  }

  const tmpCanvas = document.createElement('canvas')
  const tmpCtx = tmpCanvas.getContext('2d', { willReadFrequently: true })
  const maskCanvas = document.createElement('canvas')
  const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true })
  if (!tmpCtx || !maskCtx) {
    firstFrame.close()
    worker.terminate()
    processedTrack.stop()
    outputCanvas.remove()
    throw new Error('Failed to create processing canvases')
  }

  const paintOutput = (width: number, height: number, imageBuffer: ArrayBuffer) => {
    const imageData = new ImageData(new Uint8ClampedArray(imageBuffer), width, height)
    if (width === outputWidth && height === outputHeight) {
      outputCtx.putImageData(imageData, 0, 0)
    } else {
      // Scale into the fixed capture canvas — do not change outputCanvas.width/height.
      const scaleCanvas = document.createElement('canvas')
      scaleCanvas.width = width
      scaleCanvas.height = height
      const scaleCtx = scaleCanvas.getContext('2d')
      if (!scaleCtx) return
      scaleCtx.putImageData(imageData, 0, 0)
      outputCtx.drawImage(scaleCanvas, 0, 0, outputWidth, outputHeight)
    }
    pushFrame()
    resolveFirstWorkerPaint?.()
    resolveFirstWorkerPaint = null
  }

  let resolveFirstWorkerPaint: (() => void) | null = null
  const firstWorkerPaint = new Promise<void>((resolve) => {
    resolveFirstWorkerPaint = resolve
  })

  worker.onmessage = (e: MessageEvent) => {
    if (stopped) return
    if (e.data?.type === 'error') {
      console.warn('[background-blur]', e.data.error)
      return
    }
    if (e.data?.type !== 'renderDone') return

    const { width, height, imageBuffer } = e.data as {
      width: number
      height: number
      imageBuffer: ArrayBuffer
    }
    paintOutput(width, height, imageBuffer)
  }

  const processVideoFrame = async (videoFrame: VideoFrame) => {
    const width = videoFrame.displayWidth
    const height = videoFrame.displayHeight
    if (tmpCanvas.width !== width || tmpCanvas.height !== height) {
      tmpCanvas.width = width
      tmpCanvas.height = height
      maskCanvas.width = width
      maskCanvas.height = height
    }

    tmpCtx.drawImage(videoFrame, 0, 0, width, height)

    if (currentMode !== 'none' && segmenter) {
      if (frameCount % currentSkipFrames === 0 || !lastMaskBuffer) {
        const mlStart = performance.now()
        segmenter.segmentForVideo(tmpCanvas, performance.now(), (result) => {
          lastMaskBuffer = result.categoryMask?.getAsUint8Array() ?? null
          result.close()
        })
        const mlDuration = performance.now() - mlStart
        if (mlDuration > ML_TIME_THRESHOLD_HIGH && currentSkipFrames < 5) currentSkipFrames += 1
        else if (mlDuration < ML_TIME_THRESHOLD_LOW && currentSkipFrames > 1) currentSkipFrames -= 1
      }
      frameCount += 1
    }

    if (lastMaskBuffer) {
      const maskImageData = maskCtx.createImageData(width, height)
      const data = maskImageData.data
      for (let i = 0; i < lastMaskBuffer.length; i += 1) {
        const val = lastMaskBuffer[i]! > 0 ? 255 : 0
        const idx = i * 4
        data[idx] = val
        data[idx + 1] = val
        data[idx + 2] = val
        data[idx + 3] = 255
      }
      maskCtx.putImageData(maskImageData, 0, 0)
    } else {
      maskCtx.fillStyle = 'white'
      maskCtx.fillRect(0, 0, width, height)
    }

    const [videoBitmap, maskBitmap] = await Promise.all([
      createImageBitmap(tmpCanvas),
      createImageBitmap(maskCanvas),
    ])
    worker.postMessage({ type: 'frame', videoBitmap, maskBitmap }, [videoBitmap, maskBitmap])
    videoFrame.close()
  }

  // Seed + start processing; wait until the worker paints at least once so captureStream
  // has a real blurred frame before WebRTC attaches the track.
  outputCtx.drawImage(firstFrame, 0, 0, outputWidth, outputHeight)
  pushFrame()
  void processVideoFrame(firstFrame)

  const readFrames = async () => {
    try {
      while (!stopped) {
        const { value: videoFrame, done } = await reader.read()
        if (done || !videoFrame) break
        await processVideoFrame(videoFrame)
      }
    } catch (error) {
      if (!stopped) {
        console.warn('[background-blur] pipeline stopped', error)
      }
    }
  }

  void readFrames()

  try {
    await Promise.race([
      firstWorkerPaint,
      new Promise<void>((resolve) => {
        window.setTimeout(() => resolve(), FIRST_FRAME_TIMEOUT_MS)
      }),
    ])
  } catch {
    // continue — seed frame was already pushed
  }

  // Best-effort unmute wait after we know frames are flowing.
  // Do not return a still-muted canvas track for WebRTC — SFU remotes freeze ~30s.
  if (processedTrack.muted) {
    await new Promise<void>((resolve) => {
      const timeoutId = window.setTimeout(() => {
        processedTrack.removeEventListener('unmute', onUnmute)
        resolve()
      }, FIRST_FRAME_TIMEOUT_MS)
      const onUnmute = () => {
        window.clearTimeout(timeoutId)
        resolve()
      }
      processedTrack.addEventListener('unmute', onUnmute, { once: true })
    })
  }

  if (processedTrack.muted) {
    stopped = true
    void reader.cancel().catch(() => undefined)
    worker.terminate()
    processedTrack.stop()
    outputCanvas.remove()
    throw new Error('Blurred canvas track stayed muted; keeping raw camera')
  }

  const stream = new MediaStream([
    processedTrack,
    ...sourceStream.getAudioTracks(),
  ])

  const handle: BackgroundBlurPipelineHandle = {
    stream,
    setMode: (mode) => {
      currentMode = mode
      worker.postMessage({ type: 'changeMode', mode })
    },
    setBlurRadius: (radius) => {
      blurRadius = radius
      worker.postMessage({ type: 'changeBlurRadius', radius: String(radius) })
    },
    setBackgroundImage: (bitmap) => {
      worker.postMessage({ type: 'changeBgImage', imageBitmap: bitmap }, [bitmap])
    },
    stop: () => {
      if (stopped) return
      stopped = true
      void reader.cancel().catch(() => undefined)
      worker.terminate()
      processedTrack.stop()
      outputCanvas.remove()
    },
  }

  return handle
}
